/**
 * The address bar.
 *
 * Until now `activePath` was plain `useState('/')` and nothing ever wrote to
 * `window.location`. That is why a reload always landed on the dashboard: the
 * URL never carried which screen was open, so there was nothing for a fresh
 * mount to read back. Copying a link to a case list, opening one in a second
 * tab, and the browser's own Back button were all unavailable for the same
 * reason.
 *
 * ## Why the hash and not a real path
 *
 * `#/cases`, not `/cases`. A history-API route requires the web server to
 * answer EVERY unknown path with `index.html` — an Apache rewrite, an nginx
 * `try_files`. This application is deployed to a host whose rewrite
 * configuration this repository does not control and cannot test, and the
 * failure mode of getting it wrong is the worst one available: the SPA works
 * until the reader presses F5, at which point the server returns its own 404
 * and the application appears to have been deleted. The fragment is never
 * sent to the server, so `#/cases` is a request for `index.html` on every web
 * server that has ever existed, with no configuration at all.
 *
 * If clean URLs are wanted later, the change is confined to this module plus
 * one rewrite rule; nothing that imports it needs to know which scheme is in
 * force, which is the reason `hrefFor()` exists rather than templating `#` at
 * each of the three call sites.
 *
 * ## A fragment that is not ours is not a route
 *
 * `AppShell`'s skip link is `href="#shell-main"`. It calls `preventDefault`,
 * so it never actually writes that fragment — but an in-page anchor is a
 * shape a future contributor will reach for again, and a router that read
 * `#shell-main` as a path would answer it by navigating to the dashboard.
 * Every function here therefore requires a leading `/` and treats anything
 * else as "not a route": `readPath()` falls back to the root, and
 * `subscribe()`'s listener ignores the change entirely rather than resetting
 * the screen out from under the reader.
 */

/** The screen shown when the URL names none. */
export const ROOT_PATH = '/';

/**
 * `#/cases` -> `/cases`. Anything that is not an absolute in-app path — an
 * empty hash on first load, `#shell-main`, a bookmark fragment — is the root.
 *
 * The query string and any nested fragment are kept: no screen reads one
 * today, but discarding them here would silently break the first one that
 * does, and `App.tsx` compares against literal paths that contain neither.
 */
export function readPath(hash: string = window.location.hash): string {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw.startsWith('/')) return ROOT_PATH;
  // `/cases/` and `/cases` are the same screen; `App.tsx` only ever compares
  // against the un-slashed form, so normalise here rather than at every guard.
  const trimmed = raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;
  return trimmed;
}

/** What a nav link's `href` must be for this scheme. */
export function hrefFor(path: string): string {
  return `#${path}`;
}

/**
 * Write the path to the address bar, pushing a history entry so Back works.
 *
 * Assigning to `location.hash` rather than calling `pushState` is deliberate:
 * `pushState` does NOT fire `hashchange`, so a router built on it would need
 * every navigation to travel through React state AND every back/forward
 * through `popstate`, with two sources of truth to keep in step. Assignment
 * fires the event unconditionally, so there is exactly one path into state —
 * the listener — whether the change came from a nav click or the Back button.
 *
 * Assigning the value it already holds is a documented no-op in the HTML
 * standard (no history entry, no event), so clicking the current nav item
 * cannot stack up entries the reader then has to press Back through.
 */
export function writePath(path: string): void {
  window.location.hash = hrefFor(path);
}

/**
 * Call `onChange` whenever the URL names a different screen. Returns the
 * unsubscribe function `useEffect` wants.
 */
export function subscribe(onChange: (path: string) => void): () => void {
  function handle(): void {
    const raw = window.location.hash;
    const stripped = raw.startsWith('#') ? raw.slice(1) : raw;
    // Not our fragment: leave the current screen alone. See the docblock.
    if (!stripped.startsWith('/')) return;
    onChange(readPath(raw));
  }

  window.addEventListener('hashchange', handle);
  return () => {
    window.removeEventListener('hashchange', handle);
  };
}
